import styles from './DirectorHoverCard.module.scss';
import user from '../../../../../../assets/user.png';
import linkedin from '../../../../../../assets/linkedin.svg';
import { calendarMonthIcon, userBlack } from '../../../../../../assets/hostedassets';
import { isSafeUrl, toTitleCase } from '../../../../../../utils/utils';
import { deriveAgeFromDob, formatAgeLabel, formatAgeValue, formatDobDisplay } from '../../../../../../utils/dateUtils';

import { formatShareholdingPercent } from '../../../../../../utils/formatShareholdingPercent';
import {
    computeTenureDisplay,
    formatFollowersCount,
} from '../../../Overview/components/ManagementProfile/components/managementUserProfileUtils';
import type { DirectorHoverProfile } from './DirectorHoverCard.types';

function StatRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className={styles.StatRow}>
            <span className={styles.StatLabel}>{label}</span>
            <span className={styles.StatValue}>{value}</span>
        </div>
    );
}

function DirectorHoverCard({ profile }: { profile: DirectorHoverProfile }) {
    // Fields arrive already flattened by polus (customFields merged with its top-level fallbacks),
    // so there is no shape-guessing left to do here.
    const imageUrl = profile.imageUrl || user;
    const headline = profile.headline;
    const followers = formatFollowersCount(profile.followersCount);
    const safeLinkedInUrl = profile.linkedinUrl && isSafeUrl(profile.linkedinUrl) ? profile.linkedinUrl : undefined;

    // The two feeds disagree on both fields: polus forwards the stored values verbatim (ISO dob,
    // and no age at all — it exists on no management profile), while the Management Profile tab's
    // own rows carry a DD-MM-YYYY dob and an age. Normalise both here, in the one component both
    // feeds pass through, so a surface's date format is not a function of who fed it.
    const dobText = formatDobDisplay(profile.dob);
    const ageText = profile.age == null ? deriveAgeFromDob(profile.dob) : String(profile.age);
    const ageLabel = formatAgeLabel(ageText);
    const ageValue = formatAgeValue(ageText);

    const { durationLabel } = computeTenureDisplay(profile.tenureBeginDate, profile.tenureEndDate, profile.isActive ?? undefined);
    const currentEntitiesCount = profile.currentEntitiesCount ?? 0;
    const formerEntitiesCount = profile.formerEntitiesCount ?? 0;

    const showLinkedInCard = !!(safeLinkedInUrl || followers || headline);
    const linkedInValueLabel = followers ? `${followers} Followers` : safeLinkedInUrl ? 'View Profile' : null;
    const linkedInCardContent = (
        <>
            <div className={styles.LinkedInTopRow}>
                <span className={styles.LinkedInLabel}>LinkedIn</span>
                {linkedInValueLabel && (
                    <span className={styles.LinkedInLink}>
                        <img src={linkedin} alt="" height={14} />
                        {linkedInValueLabel}
                    </span>
                )}
            </div>
            {headline && <div className={styles.LinkedInHeadline}>{headline}</div>}
        </>
    );

    return (
        <div className={styles.DirectorHoverCard}>
            <div className={styles.Header}>
                <img className={styles.Photo} src={imageUrl} alt={profile.name ?? ''} />
                <div className={styles.HeaderText}>
                    <div className={styles.Name}>{toTitleCase(profile.name ?? '') || '-'}</div>
                    {(ageLabel || dobText || profile.gender) && (
                        <div className={styles.RailPills}>
                            {dobText ? (
                                <div className={styles.Pill}>
                                    <img src={calendarMonthIcon} alt="" className={styles.PillIcon} />
                                    <span>{`DOB: ${dobText}${ageValue ? ` (${ageValue})` : ''}`}</span>
                                </div>
                            ) : (
                                ageLabel && (
                                    <div className={styles.Pill}>
                                        <img src={calendarMonthIcon} alt="" className={styles.PillIcon} />
                                        <span>{ageLabel}</span>
                                    </div>
                                )
                            )}
                            {profile.gender && (
                                <div className={styles.Pill}>
                                    <img src={userBlack} alt="" className={styles.PillIcon} />
                                    <span>{toTitleCase(profile.gender)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showLinkedInCard &&
                (safeLinkedInUrl ? (
                    <a
                        className={styles.LinkedInCard}
                        href={safeLinkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open LinkedIn profile${followers ? ` — ${followers} followers` : ''}`}
                    >
                        {linkedInCardContent}
                    </a>
                ) : (
                    <div className={styles.LinkedInCard}>{linkedInCardContent}</div>
                ))}

            <div className={styles.Stats}>
                <StatRow label="Tenure" value={durationLabel || '-'} />
                <StatRow
                    label="Role"
                    value={
                        <span className={styles.RoleValue}>
                            {profile.designation || '-'}
                            {profile.isActive != null && (
                                <span className={profile.isActive ? styles.StatusActive : styles.StatusInactive}>
                                    {profile.isActive ? 'Active' : 'Former'}
                                </span>
                            )}
                        </span>
                    }
                />
                <StatRow label="Shareholding" value={formatShareholdingPercent(profile.shareholdingPercentage, { nullText: '-' })} />
                <StatRow
                    label="Other Directorships"
                    value={
                        <span>
                            <span className={styles.StatActive}>{currentEntitiesCount} active</span>
                            <span className={styles.StatDot} />
                            <span className={styles.StatPast}>{formerEntitiesCount} past</span>
                        </span>
                    }
                />
            </div>
        </div>
    );
}

export default DirectorHoverCard;
